/**
 * Partners Portal route — Partner management and analytics.
 * Protected route: list, create, edit, performance tracking. SSR-safe; Link for internal nav.
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import {
  usePartners,
  type PartnerAuth,
  type PartnerFilters,
} from "../../../hooks/usePartners";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getPartners } from "../../../server/sales-portal";
import type { RouterContextWithSession } from "../../../router";
import type { Partner } from "@flowstate/types";

export const Route = createFileRoute("/_admin/partners/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.partners.list(session.user.id),
      queryFn: async () => {
        const result = await getPartners({
          data: { userId: session.user.id, accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.moderate,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Partners Portal"),
  component: PartnersPortalPage,
});

function formatCurrency(amount: number, currency = "AUD") {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercentage(value: number) {
  return `${(value * 100).toFixed(1)}%`;
}

function PartnersPortalPage() {
  const { user, session, loading, isAuthenticated } = useAuth();
  const auth: PartnerAuth | null =
    user && session
      ? { userId: user.id, accessToken: session.access_token }
      : null;

  const [filters, setFilters] = useState<PartnerFilters>({});
  const [searchTerm, setSearchTerm] = useState("");

  const { data: partners = [], isLoading: partnersLoading } = usePartners(auth, {
    ...filters,
    search: searchTerm || undefined,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  // Calculate summary stats
  const stats = {
    total: partners.length,
    active: partners.filter((p) => p.status === "active").length,
    totalRevenue: partners.reduce((sum, p) => sum + (p.total_revenue_generated || 0), 0),
    totalReferrals: partners.reduce((sum, p) => sum + (p.total_referrals || 0), 0),
  };

  return (
    <main className="container mx-auto p-6 space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Partner Portal</h1>
        <Button size="sm">Add Partner</Button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total partners" value={String(stats.total)} />
        <StatCard label="Active" value={String(stats.active)} />
        <StatCard label="Total revenue" value={formatCurrency(stats.totalRevenue)} />
        <StatCard label="Total referrals" value={String(stats.totalReferrals)} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Search partners…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs"
        />
        <Select
          value={filters.partner_type ?? "all"}
          onValueChange={(v) =>
            setFilters((prev) => ({
              ...prev,
              partner_type: v === "all" ? undefined : (v as PartnerFilters["partner_type"]),
            }))
          }
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="channel">Channel</SelectItem>
            <SelectItem value="referral">Referral</SelectItem>
            <SelectItem value="reseller">Reseller</SelectItem>
            <SelectItem value="technology">Technology</SelectItem>
            <SelectItem value="strategic">Strategic</SelectItem>
            <SelectItem value="affiliate">Affiliate</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.tier ?? "all"}
          onValueChange={(v) =>
            setFilters((prev) => ({
              ...prev,
              tier: v === "all" ? undefined : (v as PartnerFilters["tier"]),
            }))
          }
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="bronze">Bronze</SelectItem>
            <SelectItem value="silver">Silver</SelectItem>
            <SelectItem value="gold">Gold</SelectItem>
            <SelectItem value="platinum">Platinum</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.status ?? "all"}
          onValueChange={(v) =>
            setFilters((prev) => ({
              ...prev,
              status: v === "all" ? undefined : (v as PartnerFilters["status"]),
            }))
          }
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Partners list */}
      {partnersLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {!partnersLoading && partners.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span className="text-4xl mb-3">🤝</span>
          <p className="text-muted-foreground text-sm">
            No partners yet. Add your first partner to get started.
          </p>
        </div>
      )}

      {!partnersLoading && partners.length > 0 && (
        <div className="space-y-3">
          {partners.map((partner) => (
            <PartnerCard key={partner.id} partner={partner} />
          ))}
        </div>
      )}
    </main>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}

function PartnerCard({ partner }: { partner: Partner }) {
  const statusColors: Record<Partner["status"], string> = {
    pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    inactive: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
    suspended: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  };

  const tierColors: Record<Partner["tier"], string> = {
    bronze: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
    silver: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
    gold: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    platinum: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  };

  return (
    <div className="rounded-lg border bg-card p-4 hover:bg-accent/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">{partner.name}</h3>
            <Badge variant="outline" className={`text-xs capitalize ${tierColors[partner.tier]}`}>
              {partner.tier}
            </Badge>
            <Badge variant="outline" className={`text-xs capitalize ${statusColors[partner.status]}`}>
              {partner.status}
            </Badge>
          </div>
          {partner.description && (
            <p className="text-sm text-muted-foreground">{partner.description}</p>
          )}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="capitalize">{partner.partner_type.replace(/_/g, " ")}</span>
            {partner.contact_email && <span>·</span>}
            {partner.contact_email && <span>{partner.contact_email}</span>}
            {partner.commission_rate && <span>·</span>}
            {partner.commission_rate && (
              <span>Commission: {formatPercentage(partner.commission_rate)}</span>
            )}
          </div>
        </div>
        <div className="text-right space-y-1">
          <p className="text-base font-semibold text-foreground">
            {formatCurrency(partner.total_revenue_generated || 0)}
          </p>
          <p className="text-xs text-muted-foreground">
            {partner.total_referrals || 0} referrals
          </p>
          <p className="text-xs text-muted-foreground">
            {partner.active_deals || 0} active deals
          </p>
        </div>
      </div>
    </div>
  );
}
